import { AccessDenied } from './AccessDenied';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, ArrowUpRight, Settings } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAccessResolver } from '@/contexts/AccessResolverContext';
import { useAuth } from '@/hooks/useAuth';

interface NoAccessProps {
  module?: string;
  showUpgrade?: boolean;
}

/**
 * NoAccess Component
 * 
 * Shows Upgrade Required UI when a module is disabled.
 * Links to /admin/modules for workspace admins to enable modules.
 * Used by ModuleGuard when a module is not enabled.
 */
export default function NoAccess({ module, showUpgrade = true }: NoAccessProps) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { plan, isAdmin } = useAccessResolver();
  const isTrial = plan?.is_trial || false;

  // Check if user can manage modules (admin/owner)
  const canManageModules =
    user?.role === 'super_admin' ||
    user?.role === 'owner' ||
    user?.role === 'admin' ||
    user?.role === 'workspace_admin' ||
    isAdmin();

  // Get module display name
  const moduleName = module
    ? module.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : 'This module';

  if (showUpgrade) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">Module Not Available</CardTitle>
            <CardDescription>
              {module
                ? `${moduleName} is not enabled for this workspace.`
                : 'This module is not enabled for this workspace.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {isTrial ? (
              <>
                <p className="text-sm text-muted-foreground">
                  You're on a trial plan. Upgrade to unlock all modules and features.
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => {
                      if (currentWorkspace?.slug) {
                        window.location.href = `/${currentWorkspace.slug}/billing`;
                      }
                    }}
                    className="w-full"
                  >
                    <span>Upgrade Plan</span>
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                  {canManageModules && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (currentWorkspace?.slug) {
                          window.location.href = `/${currentWorkspace.slug}/admin/modules`;
                        }
                      }}
                      className="w-full"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Manage Modules</span>
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {canManageModules
                    ? 'Enable this module in workspace settings to access this feature.'
                    : 'Please contact your workspace administrator to enable this feature.'}
                </p>
                {canManageModules && (
                  <Button
                    onClick={() => {
                      if (currentWorkspace?.slug) {
                        window.location.href = `/${currentWorkspace.slug}/admin/modules`;
                      }
                    }}
                    className="w-full"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Go to Module Management</span>
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AccessDenied
      title="Module Not Available"
      description={
        module
          ? `The ${moduleName} module is not enabled for this workspace. Please contact your workspace administrator to enable this feature.`
          : 'This module is not enabled for this workspace. Please contact your workspace administrator to enable this feature.'
      }
    />
  );
}
